"use client";

import { useCart } from "@/contexts/cart-context";
import {
  EventType,
  type ICartViewedEvent,
  type ICheckoutCompletedEvent,
  type ICheckoutStartedEvent,
  publishEvent,
} from "@/events";
import { CartStorage } from "@/libs/cart-storage";
import { fireUserEngagementEvent } from "@/libs/utils";
import {
  removeCartItem,
  shopifyGetCart,
  updateCartItem,
} from "@/shopify/api/shopify-cart";
import { createCheckout } from "@/shopify/api/shopify-checkout";
import { Minus, Plus, X } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useRouter } from "next/navigation";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Dynamically import the GoKwikCheckoutScript to avoid SSR issues
const GoKwikCheckoutScript = dynamic(
  () => import("@/shopify/KwikPass/GoKwikCheckoutScript"),
  { ssr: false },
);

declare global {
  interface Window {
    gokwikSdk?: {
      initCheckout: (config: any) => void;
      on: (event: string, callback: (data: any) => void) => void;
      close?: () => void;
    };
    merchantInfo?: {
      mid: string;
      environment: string;
      type: string;
      storeId: string;
      fbPixel: string;
      cart?: {
        id: string;
      };
    };
    triggerGokwikCheckout?: (cartId: string) => void;
    triggerGokwikCustomCheckout?: () => void;
    TrackierWebSDK?: any;
  }
}

export const CartDrawer: React.FC = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isOpen, closeCart, clearCart } = useCart();

  const [isAgeVerified, setIsAgeVerified] = useState(false);

  const [shopifyCartItems, setShopifyCartItems] = useState<any[]>([]);

  const shopifySubtotal = shopifyCartItems.reduce(
    (total: number, item: any) => {
      return (
        total +
        Number.parseFloat(item.merchandise?.price?.amount || "0") *
          item.quantity
      );
    },
    0,
  );

  const triggerTrackierTrackConv = useCallback((order: any) => {
    const orderId = order?.order_name || order.orderSuccessBody?.order_name;
    const orderAmount =
      order?.total_price || order.orderSuccessBody?.total_price;
    window.TrackierWebSDK.trackConv(
      "track.techtrack.in",
      "6776449c8e7dea356102ea94",
      {
        is_camp: true,
        txn_id: orderId,
        sale_amount: orderAmount,
        is_iframe: true,
      },
    );
  }, []);

  const onCartItemContainerScroll = (event: React.UIEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
  };

  const triggeredCheckoutStatedEvent = () => {
    const event: ICheckoutStartedEvent = {
      type: EventType.CHECKOUT_STARTED,
      cartValue: shopifySubtotal,
      currency: "INR",
      timestamp: Date.now(),
      itemCount: shopifyCartItems.length,
      items: shopifyCartItems.map((item) => ({
        productId: item.merchandise?.product?.id || "",
        productName: item.merchandise?.product?.title || "",
        price: Number.parseFloat(item.merchandise?.price?.amount || "0"),
        quantity: item.quantity,
        variant: item.merchandise?.title || "",
      })),
    };
    publishEvent(event);
  };

  const triggeredCheckoutCompleteEvent = (order: any) => {
    console.log("orderSuccess triggeredCheckoutCompleteEvent", order);
    const eventPayload: ICheckoutCompletedEvent = {
      type: EventType.CHECKOUT_COMPLETED,
      orderId: order?.merchant_order_id || "",
      cartValue: shopifySubtotal,
      currency: "INR",
      itemCount: shopifyCartItems.length,
      timestamp: Date.now(),
      items: shopifyCartItems.map((item) => ({
        productId: item.merchandise?.product?.id || "",
        productName: item.merchandise?.product?.title || "",
        price: Number.parseFloat(item.merchandise?.price?.amount || "0"),
        quantity: item.quantity,
        variant: item.merchandise?.title || "",
      })),
    };
    // Publish the checkout completed event
    publishEvent(eventPayload);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Functions are stable from context
  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleOrderSuccess = (event: any) => {
        if (event?.data?.type === "orderSuccess") {
          console.log("orderSuccess event recieved", event);

          const order = event.data.response;

          if (typeof window !== "undefined" && window.TrackierWebSDK) {
            triggerTrackierTrackConv(order);
          }

          triggeredCheckoutCompleteEvent(order);

          CartStorage.clear();
          clearCart();
          closeCart();
        }
      };

      window.addEventListener("message", handleOrderSuccess);
      console.log("orderSuccess event added");
      return () => {
        window.removeEventListener("message", handleOrderSuccess);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manage document body overflow and handle checkout popup close events
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;

    // Set overflow when cart opens/closes
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalStyle;
    }

    // Handle message events from checkout popup
    const onCheckoutPopupClose = (event: any) => {
      const messageType = event?.data?.type;

      if (messageType === "close" && isOpen) {
        requestAnimationFrame(() => {
          document.body.style.overflow = "hidden";
        });
      }
    };

    window.addEventListener("message", onCheckoutPopupClose);

    // Cleanup function
    return () => {
      document.body.style.overflow = originalStyle;
      window.removeEventListener("message", onCheckoutPopupClose);
    };
  }, [isOpen]);

  // Handle BotPenguin chatbot widget visibility when cart opens/closes
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("cart-open");
    } else {
      document.body.classList.remove("cart-open");
    }

    // Cleanup function
    return () => {
      document.body.classList.remove("cart-open");
    };
  }, [isOpen]);

  useEffect(() => {
    const savedItems = CartStorage.getShopifyCartItems();
    setShopifyCartItems(savedItems);
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const savedItems = CartStorage.getShopifyCartItems();

      setShopifyCartItems(savedItems);
    };

    const interval = setInterval(handleStorageChange, 100);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const fetchShopifyCartItems = async () => {
        try {
          const cartId = CartStorage.getCartToken();
          if (cartId) {
            const cartData = await shopifyGetCart(cartId);

            if (cartData?.id) {
              CartStorage.saveCartToken(cartData.id);
            }

            if (cartData?.lines?.edges && cartData.lines.edges.length > 0) {
              const shopifyItems = cartData.lines.edges.map(
                (edge: any) => edge.node,
              );
              setShopifyCartItems(shopifyItems);

              CartStorage.saveShopifyCartItems(shopifyItems);

              // Fire cart viewed event for Shopify Analytics
              const cartViewedEvent: ICartViewedEvent = {
                type: EventType.CART_VIEWED,
                cartId: cartData.id,
                products: cartData.lines.edges.map((edge: any) => ({
                  variantId: edge.node.merchandise.id,
                  price: Number.parseFloat(edge.node.merchandise.price.amount),
                  quantity: edge.node.quantity,
                })),
                timestamp: Date.now(),
              };
              publishEvent(cartViewedEvent);
            } else {
              const savedItems = CartStorage.getShopifyCartItems();
              if (savedItems.length > 0) {
                setShopifyCartItems(savedItems);

                // Fire cart viewed event for saved items too
                if (cartData?.id) {
                  const cartViewedEvent: ICartViewedEvent = {
                    type: EventType.CART_VIEWED,
                    cartId: cartData.id,
                    products: savedItems.map((item: any) => ({
                      variantId: item.merchandise.id,
                      price: Number.parseFloat(item.merchandise.price.amount),
                      quantity: item.quantity,
                    })),
                    timestamp: Date.now(),
                  };
                  publishEvent(cartViewedEvent);
                }
              }
            }
          }
        } catch (error) {
          console.error("Error fetching Shopify cart:", error);
        }
      };

      fetchShopifyCartItems();
    }
  }, [isOpen]);

  const onCheckout = async () => {
    triggeredCheckoutStatedEvent();

    // Track Started Checkout GK event
    publishEvent({
      type: EventType.STARTED_CHECKOUT_GK,
      cartValue: shopifySubtotal,
      currency: "INR",
    });

    try {
      const response = await createCheckout();
      if (response?.id) {
        console.log(response?.id, "CART ID");
        // Attach event listeners for checkout events

        // Trigger the GoKwik checkout with the formatted cart ID
        if (window.triggerGokwikCheckout) {
          window.triggerGokwikCheckout(response?.id);
        } else {
          console.error("triggerGokwikCheckout function not available");
        }
      }
    } catch (error) {
      console.error("Checkout error", error);
    }
  };

  const loyaltyPoints = Math.floor(shopifySubtotal * 0.2);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999999] overflow-hidden">
      {/* Load the GoKwik Checkout Script */}
      <GoKwikCheckoutScript />

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={closeCart}
      />

      {/* Cart drawer */}
      <div className="absolute inset-y-0 right-0 max-w-full flex justify-end">
        <div className="relative w-[90%] sm:w-screen sm:max-w-md transform transition-transform duration-300 ease-in-out origin-right">
          <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto border-l border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 ">
              <h2 className="text-[24px] font-system text-[#000000] font-bold text-center w-full">
                {t("cartDrawer.cart")}
              </h2>
              <button
                id="close-cart"
                onClick={closeCart}
                className="text-[red] absolute right-4"
                aria-label="Close cart"
              >
                <X className="h-8 w-8" />
              </button>
            </div>

            {/* Cart items */}
            <div
              className="flex-1 px-4 overflow-auto"
              onScroll={onCartItemContainerScroll}
            >
              {shopifyCartItems.length === 0 ? (
                <div className="text-center flex flex-col items-center justify-center h-full py-20">
                  <h3 className="text-[17px] font-Poppins p-4 text-[#000000]">
                    {t("cartDrawer.emptyCart")}
                  </h3>
                </div>
              ) : (
                <>
                  <div className="h-[1px] bg-gray-200 my-4" />
                  <div className="space-y-4">
                    {shopifyCartItems.map((item: any, index: number) => (
                      <div
                        key={`shopify-${item.id}-${index}`}
                        className="border-b pb-4"
                      >
                        <div className="flex items-start">
                          <div className="h-20 w-20 flex-shrink-0 overflow-hidden border border-gray-200">
                            {item.merchandise?.image?.url ? (
                              <Image
                                src={item.merchandise.image.url}
                                alt={
                                  item.merchandise.product.title ||
                                  "Product image"
                                }
                                width={64}
                                height={64}
                                loading="lazy"
                                className="h-full w-full object-cover object-center"
                              />
                            ) : (
                              <div className="h-full w-full bg-gray-200 flex items-center justify-center">
                                <span>{t("cartDrawer.noImage")}</span>
                              </div>
                            )}
                          </div>

                          <div className="ml-4 flex-1">
                            <div className="flex justify-between">
                              <h3 className="text-[17px] font-Poppins text-[#000000] ">
                                {item.merchandise?.product?.title || "Product"}
                              </h3>
                            </div>
                            <p className="mt-1 text-[15.3px] font-Poppins text-[#000000] ">
                              {item.merchandise?.title && (
                                <>
                                  {t("cartDrawer.packSize")}{" "}
                                  {item.merchandise.title}
                                </>
                              )}
                            </p>
                            <div className="flex items-center justify-between mt-4">
                              <div className="relative">
                                <div className="flex items-center border p-1">
                                  <button
                                    id="add-to-cart"
                                    aria-label="add-to-cart"
                                    className="p-1 px-2"
                                    data-test-id="decrease-quantity"
                                    onClick={async () => {
                                      fireUserEngagementEvent();
                                      try {
                                        const currentItems =
                                          CartStorage.getShopifyCartItems();
                                        let updatedItems: any[];

                                        if (item.quantity > 1) {
                                          updatedItems = currentItems.map(
                                            (localItem: any) =>
                                              localItem.id === item.id
                                                ? {
                                                    ...localItem,
                                                    quantity:
                                                      localItem.quantity - 1,
                                                  }
                                                : localItem,
                                          );
                                        } else {
                                          updatedItems = currentItems.filter(
                                            (localItem: any) =>
                                              localItem.id !== item.id,
                                          );
                                        }

                                        setShopifyCartItems(updatedItems);
                                        CartStorage.saveShopifyCartItems(
                                          updatedItems,
                                        );

                                        try {
                                          const cartId =
                                            CartStorage.getCartToken();
                                          if (cartId && item.quantity > 1) {
                                            await updateCartItem(
                                              item.id,
                                              item.quantity - 1,
                                              cartId,
                                            );
                                          } else if (
                                            cartId &&
                                            item.quantity === 1
                                          ) {
                                            await removeCartItem(
                                              item.id,
                                              cartId,
                                            );
                                          }
                                        } catch (error) {
                                          console.error(
                                            "Background sync failed:",
                                            error,
                                          );
                                        }
                                      } catch (error) {
                                        console.error(
                                          "Error updating Shopify cart:",
                                          error,
                                        );
                                      }
                                    }}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>

                                  <span className="px-2">{item.quantity}</span>
                                  <button
                                    id="increment-count"
                                    aria-label="increment-count"
                                    className="p-1 px-2"
                                    data-test-id="increase-quantity"
                                    onClick={async () => {
                                      fireUserEngagementEvent();
                                      try {
                                        const currentItems =
                                          CartStorage.getShopifyCartItems();
                                        const updatedItems = currentItems.map(
                                          (localItem: any) =>
                                            localItem.id === item.id
                                              ? {
                                                  ...localItem,
                                                  quantity:
                                                    localItem.quantity + 1,
                                                }
                                              : localItem,
                                        );

                                        setShopifyCartItems(updatedItems);
                                        CartStorage.saveShopifyCartItems(
                                          updatedItems,
                                        );

                                        try {
                                          const cartId =
                                            CartStorage.getCartToken();
                                          if (cartId) {
                                            await updateCartItem(
                                              item.id,
                                              item.quantity + 1,
                                              cartId,
                                            );
                                          }
                                        } catch (error) {
                                          console.error(
                                            "Background sync failed:",
                                            error,
                                          );
                                        }
                                      } catch (error) {
                                        console.error(
                                          "Error updating Shopify cart:",
                                          error,
                                        );
                                      }
                                    }}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>

                              <p className="text-base font-medium text-gray-900">
                                Rs.{" "}
                                {Number.parseFloat(
                                  item.merchandise?.price?.amount || "0",
                                ).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            {shopifyCartItems.length > 0 && (
              <div className="border-t border-gray-200 px-4 py-4">
                {/* Age verification */}
                <div className="mb-4 p-3 shadow-md bg-white rounded-md text-center">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      id="age-verification"
                      data-test-id="age-verification-checkbox"
                      checked={isAgeVerified}
                      onChange={() => setIsAgeVerified(!isAgeVerified)}
                      className="h-3 w-3 md:h-4 md:w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="age-verification"
                      className="ml-2 text-[16px] md:text-[20px] font-semibold font-Poppins text-black"
                    >
                      {t("cartDrawer.ageVerification")}
                    </label>
                  </div>
                  <p className="text-[12px] font-Poppins text-[#ff0000] text-center mt-1">
                    {t("cartDrawer.pleaseTickBeforeProceeding")}
                  </p>
                </div>

                {/* Subtotal */}
                <div className="flex justify-between mb-2">
                  <p className="text-[20px] font-system text-[#202020]">
                    {t("cartDrawer.subtotal")}
                  </p>
                  <p className="text-[20px] font-Poppins text-gray-900">
                    Rs. {shopifySubtotal.toFixed(2)}
                  </p>
                </div>

                {/* Loyalty points */}
                <div
                  className="border border-[black] rounded-md p-3 mb-4 text-center"
                  data-test-id="loyalty-points"
                >
                  <div className="flex justify-center items-center">
                    <span className="font-semibold text-[#000000] text-[14px] font-Poppins">
                      {t("cartDrawer.earn")}{" "}
                    </span>
                    <span className="inline-flex items-center bg-orange-100 text-orange-800 px-2 rounded-full font-bold mx-1">
                      <span className="text-xs mr-1">🔥</span>
                      <span>{loyaltyPoints}</span>
                    </span>
                    <span className="font-semibold text-[#000000] text-[14px] font-Poppins">
                      {" "}
                      {t("cartDrawer.coinsOnThisCart")}
                    </span>
                  </div>
                  <p className="font-semibold text-[#000000] text-[11px] font-Poppins mt-3">
                    {t("cartDrawer.loyaltyDiscount")}
                  </p>
                </div>

                <p className="text-[11.7px] font-Poppins text-[#000000] text-center mb-3">
                  {t("cartDrawer.shippingTaxesNote")}
                </p>

                {/* Checkout button */}
                <div className="w-full">
                  <button
                    id="checkout"
                    aria-label="checkout"
                    onClick={onCheckout}
                    disabled={!isAgeVerified}
                    className={`w-full flex flex-col items-center justify-center rounded-md border border-transparent ${
                      isAgeVerified
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-gray-300 cursor-not-allowed"
                    } px-6 py-3 text-base font-medium text-white shadow-sm relative`}
                    data-test-id="checkout-button"
                  >
                    <div className="flex items-center space-x-2">
                      <span>{t("cartDrawer.checkout")}</span>
                      <Image
                        src="/images/ppg.png"
                        alt="Payment Options"
                        width={100}
                        height={32}
                        loading="lazy"
                        className="h-8 w-[100px]"
                      />
                    </div>
                    {/* Text below Checkout and logo */}
                    <p className="text-xs text-gray-200 mt-1">
                      {t("cartDrawer.prepaidDiscount")}
                    </p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
